/**
 * PublicDeliverySurvey — /dostawa/:location
 * Premium mobile-first design. Bez logowania.
 * Klient skanuje QR → ankieta (rating / choice / text) → kupon.
 * Limit 1 ankieta / urządzenie (deviceId w localStorage).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Star, Check, Copy, ChevronDown, ArrowRight } from 'lucide-react';
import LanguageSwitch from './LanguageSwitch';
import { getLang, setLang, useT } from './surveyI18n';
import './PublicDeliverySurvey.css';

const API = process.env.REACT_APP_BACKEND_URL;
const DEVICE_KEY = 'mrj_delivery_device_id';

function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = (crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now());
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

const RATING_LABELS = ['Bardzo źle', 'Słabo', 'Średnio', 'Dobrze', 'Super'];

export default function PublicDeliverySurvey() {
  const { location } = useParams();
  const [lang, setLangState] = useState(getLang());
  const t = useT(lang);
  const changeLang = (l) => { setLang(l); setLangState(l); };
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [showContact, setShowContact] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/api/public-delivery-survey/${location}`)
      .then((r) => setConfig(r.data))
      .catch((e) =>
        setError(e?.response?.data?.detail || 'Nie udało się załadować ankiety')
      );
  }, [location]);

  // questions that must be answered to enable the submit button
  // text questions are always optional
  const requiredQuestions = useMemo(() => {
    if (!config) return [];
    return config.questions.filter((q) => q.type !== 'text');
  }, [config]);

  const answeredRequired = useMemo(
    () =>
      requiredQuestions.filter(
        (q) => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== ''
      ).length,
    [requiredQuestions, answers]
  );
  const totalRequired = requiredQuestions.length;
  const progressPct = totalRequired === 0 ? 0 : Math.round((answeredRequired / totalRequired) * 100);
  const canSubmit = answeredRequired === totalRequired && !submitting;

  const setAnswer = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));
  const setComment = (qid, val) => setComments((c) => ({ ...c, [qid]: val }));

  const submit = async () => {
    if (!config || !canSubmit) return;
    const ansList = config.questions
      .map((q) => {
        const v = answers[q.id];
        if (v === undefined || v === null || v === '') return null;
        let valueLabel = '';
        if (q.type === 'choice' && Array.isArray(q.options)) {
          const opt = q.options.find((o) => o.value === v);
          valueLabel = opt ? `${opt.emoji ? opt.emoji + ' ' : ''}${opt.label}` : v;
        }
        const comment = q.allowComment ? (comments[q.id] || '').trim() : '';
        return { id: q.id, type: q.type, label: q.label, value: v, valueLabel, comment };
      })
      .filter(Boolean);

    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/api/public-delivery-survey/${location}/submit`,
        {
          deviceId: getOrCreateDeviceId(),
          answers: ansList,
          contact_name: contactName,
          contact_phone: contactPhone,
        }
      );
      setResult(r.data);
      // RODO audit trail
      axios.post(`${API}/api/legal/consents`, {
        context: 'form:delivery-survey',
        documentTypes: ['regulamin', 'privacy'],
        userPhone: contactPhone || null,
        formPayloadSummary: `${contactName || 'anonim'} / ${contactPhone || '-'}`,
      }).catch(() => {});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Coś poszło nie tak. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = async () => {
    if (!result?.couponCode) return;
    try {
      await navigator.clipboard.writeText(result.couponCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {/* noop */}
  };

  // -------------------- ERROR ---------------------
  if (error && !result && !config) {
    return (
      <div className="mjds-root">
        <LanguageSwitch value={lang} onChange={changeLang} />
        <div className="mjds-shell">
          <div className="mjds-brand">MR JERRY</div>
          <div className="mjds-error" data-testid="public-error">
            <div className="icon">🚫</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t.ups}</div>
            <div style={{ fontSize: 13 }}>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  // -------------------- LOADING -------------------
  if (!config && !result) {
    return (
      <div className="mjds-root">
        <LanguageSwitch value={lang} onChange={changeLang} />
        <div className="mjds-shell">
          <div className="mjds-brand">MR JERRY</div>
          <div className="mjds-loading">
            <div><span className="dot" /><span className="dot" /><span className="dot" /></div>
            <div style={{ marginTop: 12 }}>{t.loading}</div>
          </div>
        </div>
      </div>
    );
  }

  // -------------------- SUCCESS -------------------
  if (result) {
    return (
      <div className="mjds-root">
        <LanguageSwitch value={lang} onChange={changeLang} />
        <div className="mjds-shell">
          <div className="mjds-brand">MR JERRY</div>
          <div className="mjds-success" data-testid="public-success">
            <div className="mjds-celebrate">🎉</div>
            <div className="mjds-thanks">{t.thanks}</div>
            {result.couponCode ? (
              <>
                <div className="mjds-reward-title" data-testid="public-coupon-title">
                  🎁 {result.coupon_title || 'Kupon rabatowy'}
                </div>
                <div className="mjds-reward-desc" data-testid="public-coupon-description">
                  {result.coupon_description || 'Wpisz kod przy następnym zamówieniu online.'}
                </div>
                <div className="mjds-code-label">{t.your_code}</div>
                <div className="mjds-code" data-testid="public-coupon-code">{result.couponCode}</div>
                <button onClick={copyCode}
                        data-testid="public-copy-code"
                        className={`mjds-copy-btn ${copied ? 'copied' : ''}`}>
                  {copied
                    ? (<><Check size={16} /> {t.copied}</>)
                    : (<><Copy size={16} /> {t.copy_code}</>)}
                </button>
                {result.redeem_url && (
                  <a href={result.redeem_url} target="_blank" rel="noreferrer"
                     className="mjds-redeem"
                     data-testid="public-redeem-link">
                    {t.order_with_discount} <ArrowRight size={18} />
                  </a>
                )}
              </>
            ) : (
              <div style={{ color: '#fbbf24', padding: '14px 0', fontSize: 14 }}>
                {t.no_coupon_msg}
              </div>
            )}
            <div className="mjds-footer" style={{ marginTop: 20 }}>
              MR JERRY · <span>dziękujemy że jesteś z nami</span>
            </div>
            <div style={{ marginTop: 10, textAlign: 'center', fontSize: 11, color: 'var(--text-muted, #94a3b8)' }}>
              Zgodnie z{' '}
              <a href="/regulamin-konkursu" target="_blank" rel="noreferrer" style={{ color: '#d4af37', textDecoration: 'none', fontWeight: 700 }}>
                Regulaminem konkursu
              </a>.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -------------------- FORM ----------------------
  return (
    <div className="mjds-root">
      <LanguageSwitch value={lang} onChange={changeLang} />
      <div className="mjds-shell">
        <div className="mjds-brand">MR JERRY</div>
        <div className="mjds-hero">
          <span className="emoji">🛵</span>{t.hero_delivery}
        </div>
        <p className="mjds-subhero">
          {t.brand_sub_delivery}
        </p>

        {/* Progress */}
        <div className="mjds-progress-wrap">
          <div className="mjds-progress-meta">
            <span>{t.progress}</span>
            <span data-testid="public-progress-text">{answeredRequired} / {totalRequired}</span>
          </div>
          <div className="mjds-progress-bar">
            <div className="mjds-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Questions */}
        {config.questions.map((q, idx) => (
          <div className="mjds-card" key={q.id} style={{ animationDelay: `${idx * 60}ms` }}>
            <div className="mjds-q-idx">{t.question_label} {idx + 1}{q.type === 'text' ? ` · ${t.optional_badge}` : ''}</div>
            <div className="mjds-q-label">{q.label}</div>
            {q.hint && <div className="mjds-q-hint">{q.hint}</div>}

            {q.type === 'rating' && (
              <div className="mjds-stars" role="radiogroup" aria-label={q.label}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const selected = answers[q.id] === n;
                  const filled = answers[q.id] && n <= answers[q.id];
                  return (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setAnswer(q.id, n)}
                      className={`mjds-star-btn ${selected ? 'is-selected' : ''} ${filled ? 'is-filled' : ''}`}
                      data-testid={`q-${q.id}-${n}`}
                      title={RATING_LABELS[n - 1]}
                    >
                      <Star size={22} />
                      <span className="mjds-star-num">{n}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {q.type === 'choice' && Array.isArray(q.options) && (
              <div className="mjds-choices" role="radiogroup" aria-label={q.label}>
                {q.options.map((opt) => {
                  const selected = answers[q.id] === opt.value;
                  const tone = opt.tone || 'neutral';
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setAnswer(q.id, opt.value)}
                      className={`mjds-choice-btn tone-${tone} ${selected ? 'is-selected' : ''}`}
                      data-testid={`q-${q.id}-${opt.value}`}
                    >
                      {opt.emoji && <span className="emoji">{opt.emoji}</span>}
                      <span className="label">{opt.label}</span>
                      <span className="check"><Check size={14} strokeWidth={3} /></span>
                    </button>
                  );
                })}
              </div>
            )}

            {q.type === 'text' && (
              <textarea
                className="mjds-textarea"
                value={answers[q.id] || ''}
                onChange={(e) => setAnswer(q.id, e.target.value)}
                placeholder={t.text_placeholder_delivery}
                maxLength={1000}
                data-testid={`q-${q.id}-text`}
              />
            )}

            {/* Optional per-question comment (allowComment) */}
            {q.allowComment && q.type !== 'text' && (
              <div style={{ marginTop: 12 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                  letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {t.add_comment}
                </div>
                <textarea
                  className="mjds-textarea"
                  value={comments[q.id] || ''}
                  onChange={(e) => setComment(q.id, e.target.value)}
                  placeholder={t.comment_placeholder}
                  maxLength={500}
                  rows={2}
                  style={{ minHeight: 60 }}
                  data-testid={`q-${q.id}-comment`}
                />
              </div>
            )}
          </div>
        ))}

        {/* Contact */}
        <div className="mjds-card">
          <button
            type="button"
            className="mjds-contact-toggle"
            onClick={() => setShowContact((v) => !v)}
            data-testid="public-contact-toggle"
            style={{ width: '100%' }}>
            📞&nbsp; {showContact ? t.contact_hide : t.contact_show_delivery}
            <ChevronDown
              size={16}
              style={{ marginLeft: 'auto', transition: 'transform 220ms', transform: showContact ? 'rotate(180deg)' : 'none' }}
            />
          </button>
          {showContact && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                {t.contact_hint_delivery}
              </div>
              <input
                className="mjds-input"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder={t.name}
                data-testid="public-contact-name"
              />
              <input
                className="mjds-input"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder={t.phone}
                inputMode="tel"
                data-testid="public-contact-phone"
              />
            </div>
          )}
        </div>

        {error && (
          <div className="mjds-error" style={{ marginBottom: 14 }} data-testid="public-submit-error">
            {error}
          </div>
        )}

        <button
          className="mjds-cta"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="submit-survey">
          {submitting
            ? t.submitting
            : canSubmit
              ? (<>{t.submit_delivery}</>)
              : (<>{t.answer_n_questions(totalRequired - answeredRequired)}</>)}
        </button>

        <div className="mjds-footer">
          {t.footer_delivery}
        </div>
      </div>
    </div>
  );
}
